// Cola de sincronización de aplicaciones creadas offline. Se dispara desde
// varios lugares (evento online, apertura de la app, vuelta a foreground,
// botón manual) — todos llaman a la misma función `trySync`, nunca se
// depende solo de Background Sync (poco fiable en iOS/Safari).
import {
  listPendingAplicacionesByUser,
  updatePendingAplicacionStatus,
  removePendingAplicacion,
  addPendingAplicacion,
} from './offlineDb';
import { crearAplicacion } from '../aplicaciones';
import { isOnline } from './networkStatus';

let syncing = false;

function notifyChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('fg-offline-change'));
  }
}

function generateClientOperationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback simple para navegadores muy antiguos sin crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Punto de entrada único para "Confirmar y guardar" en el wizard de Nueva
// aplicación: decide si crea online o encola offline, sin que la página
// tenga que conocer esa lógica.
//
// - Sin conexión (navigator.onLine=false): guarda localmente de una, sin
//   intentar la red (evita el timeout/():para qué esperar un fetch que ya
//   sabemos que va a fallar).
// - Con conexión pero el fetch falla por red real (networkError): mismo
//   camino, la operación queda PENDIENTE para sincronizar después.
// - Error de validación/negocio (con respuesta real del servidor): se
//   propaga tal cual, NO se encola (guardar localmente un dato inválido no
//   tiene sentido, solo se sabrá que es inválido al reintentar).
export async function createAplicacionOfflineFirst(authUserId, payload) {
  const clientOperationId = generateClientOperationId();

  const saveLocally = async () => {
    await addPendingAplicacion({
      client_operation_id: clientOperationId,
      auth_user_id: authUserId,
      payload,
      estado_local: 'PENDIENTE',
      error_message: null,
      created_at_local: new Date().toISOString(),
    });
    notifyChange();
    return { ok: true, offline: true, clientOperationId };
  };

  if (!isOnline()) {
    return saveLocally();
  }

  const result = await crearAplicacion({ ...payload, client_operation_id: clientOperationId });
  if (result.ok) {
    return { ok: true, offline: false, id: result.id };
  }
  if (result.networkError) {
    return saveLocally();
  }
  return result;
}

export function isSyncing() {
  return syncing;
}

// Intenta sincronizar todo lo PENDIENTE/ERROR de un usuario. Sincrónico por
// ítem (uno a la vez) para no disparar N creaciones concurrentes contra la
// misma cola; si uno falla, sigue con los demás.
export async function trySync(authUserId) {
  if (!authUserId || syncing || !isOnline()) {
    return { attempted: 0, synced: 0 };
  }

  syncing = true;
  notifyChange();
  let attempted = 0;
  let synced = 0;

  try {
    const pending = await listPendingAplicacionesByUser(authUserId);
    const toSync = pending.filter((item) => item.estado_local === 'PENDIENTE' || item.estado_local === 'ERROR');

    for (const item of toSync) {
      attempted += 1;
      await updatePendingAplicacionStatus(item.client_operation_id, {
        estado_local: 'SINCRONIZANDO',
        error_message: null,
      });
      notifyChange();

      const result = await crearAplicacion({
        ...item.payload,
        client_operation_id: item.client_operation_id,
      });

      if (result.ok) {
        await removePendingAplicacion(item.client_operation_id);
        synced += 1;
      } else {
        await updatePendingAplicacionStatus(item.client_operation_id, {
          estado_local: 'ERROR',
          error_message: result.message,
        });
      }
      notifyChange();
    }
  } finally {
    syncing = false;
    notifyChange();
  }

  return { attempted, synced };
}
